import React, { useState, useMemo } from "react";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import AuthBoxedLayout from "../../components/Auth/AuthBoxedLayout";
import AuthField from "../../components/Auth/AuthField";
import EyeIcon from "../../components/Auth/EyeIcon";

const PASSWORD_MIN_LEN = 8;
const PASSWORD_HAS_UPPERCASE = /[A-Z]/;
const PASSWORD_HAS_SPECIAL = /[^A-Za-z0-9]/;

function validatePassword(pwd) {
	if (!pwd || pwd.length < PASSWORD_MIN_LEN) {
		return { valid: false, key: "signup.form.passwordHint" };
	}
	if (!PASSWORD_HAS_UPPERCASE.test(pwd)) {
		return { valid: false, key: "signup.form.passwordWeak" };
	}
	if (!PASSWORD_HAS_SPECIAL.test(pwd)) {
		return { valid: false, key: "signup.form.passwordWeak" };
	}
	return { valid: true };
}

function ResetPassword() {
	const history = useHistory();
	const location = useLocation();
	const token = useMemo(
		() => new URLSearchParams(location.search).get("token") || "",
		[location.search]
	);

	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const [passwordError, setPasswordError] = useState("");

	const handlSubmit = async (e) => {
		e.preventDefault();
		setPasswordError("");

		if (!token) {
			toast.error(i18n.t("resetPassword.errors.invalidLink"));
			return;
		}
		if (password !== confirmPassword) {
			setPasswordError(i18n.t("resetPassword.form.passwordMismatch"));
			return;
		}
		const pwdCheck = validatePassword(password);
		if (!pwdCheck.valid) {
			setPasswordError(i18n.t(pwdCheck.key));
			return;
		}

		setLoading(true);
		try {
			await api.post("/auth/reset-password", { token, password });
			toast.success(i18n.t("resetPassword.toasts.success"));
			history.push("/login");
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
	};

	if (!token) {
		return (
			<AuthBoxedLayout
				title={i18n.t("resetPassword.title")}
				titleClassName="text-primary"
			>
				<p className="text-sm text-danger mb-6">{i18n.t("resetPassword.errors.invalidLink")}</p>
				<RouterLink
					to="/recoverPassword"
					className="text-primary font-semibold underline"
				>
					{i18n.t("resetPassword.buttons.requestNew")}
				</RouterLink>
			</AuthBoxedLayout>
		);
	}

	return (
		<AuthBoxedLayout
			testId="reset-password-page"
			title={i18n.t("resetPassword.title")}
			subtitle={i18n.t("resetPassword.subTitle")}
			titleClassName="text-primary"
		>
			<form noValidate onSubmit={handlSubmit}>
				<AuthField
					id="password"
					label={i18n.t("resetPassword.form.password")}
					error={passwordError}
					hint={!passwordError ? i18n.t("signup.form.passwordHint") : undefined}
				>
					<div className="relative">
						<input
							type={showPassword ? "text" : "password"}
							id="password"
							name="password"
							className="form-input !pr-10"
							placeholder={i18n.t("resetPassword.form.passwordPlaceholder")}
							value={password}
							onChange={(e) => {
								setPassword(e.target.value);
								setPasswordError("");
							}}
							autoComplete="new-password"
							autoFocus
							required
						/>
						<button
							type="button"
							className="absolute right-0 top-0 flex h-[2.3125rem] items-center px-3 text-default-500 hover:text-default-800"
							aria-label="toggle password visibility"
							onClick={() => setShowPassword((prev) => !prev)}
						>
							<EyeIcon open={showPassword} />
						</button>
					</div>
				</AuthField>

				<AuthField
					id="confirmPassword"
					label={i18n.t("resetPassword.form.confirmPassword")}
				>
					<input
						type={showPassword ? "text" : "password"}
						id="confirmPassword"
						name="confirmPassword"
						className="form-input"
						placeholder={i18n.t("resetPassword.form.confirmPasswordPlaceholder")}
						value={confirmPassword}
						onChange={(e) => setConfirmPassword(e.target.value)}
						autoComplete="new-password"
						required
					/>
				</AuthField>

				<div className="mt-6 text-center">
					<button
						type="submit"
						className="btn bg-primary text-white w-full disabled:opacity-60"
						disabled={loading}
					>
						{i18n.t("resetPassword.buttons.submit")}
					</button>
				</div>

				<div className="mt-6 text-center">
					<RouterLink
						to="/login"
						className="text-primary font-semibold underline hover:text-primary/80"
					>
						{i18n.t("resetPassword.buttons.returnLogin")}
					</RouterLink>
				</div>
			</form>
		</AuthBoxedLayout>
	);
}

export default ResetPassword;
